// ForgotPassword.js
import React, { useState } from "react";
import { API_BASE_URL } from './config.js';
import axios from "axios";
import { useNavigate, Link } from "react-router-dom";
import { 
  Box, 
  TextField, 
  Button, 
  Typography, 
  Container,
  Paper,
  Alert,
  InputAdornment,
  CircularProgress,
  Stepper,
  Step,
  StepLabel,
  StepContent
} from '@mui/material';
import { 
  EmailOutlined,
  LockOutlined,
  CheckCircleOutline,
  ArrowBack
} from '@mui/icons-material';

function ForgotPassword() {
  const [activeStep, setActiveStep] = useState(0);
  const [email, setEmail] = useState("");
  const [resetCode, setResetCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  const steps = ['Request Reset Code', 'Verify Code', 'Reset Password'];

  const handleRequestCode = async (e) => {
    e.preventDefault();
    setError("");
    setIsLoading(true);

    try {
      const response = await axios.post(`${API_BASE_URL}/api/forgot-password/`, {
        email,
      });
      setSuccess(response.data.message);
      setActiveStep(1);
    } catch (err) {
      setError(err.response?.data?.error || "Failed to send reset code");
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerifyCode = async (e) => {
    e.preventDefault();
    setError("");
    setIsLoading(true);

    try {
      const response = await axios.post(`${API_BASE_URL}/api/verify-reset-code/`, {
        email,
        code: resetCode,
      });
      setSuccess(response.data.message);
      setActiveStep(2);
    } catch (err) {
      setError(err.response?.data?.error || "Invalid verification code");
    } finally {
      setIsLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setError("");
    
    if (newPassword !== confirmPassword) {
      setError("Passwords do not match");
      return;
    }

    if (newPassword.length < 8) {
      setError("Password must be at least 8 characters long");
      return;
    }

    setIsLoading(true);

    try {
      const response = await axios.post(`${API_BASE_URL}/api/reset-password/`, {
        email,
        code: resetCode,
        new_password: newPassword,
      });
      setSuccess("Password reset successfully! Redirecting to login...");
      
      // Redirect to login after 3 seconds
      setTimeout(() => {
        navigate('/');
      }, 3000);
    } catch (err) {
      setError(err.response?.data?.error || "Failed to reset password");
    } finally {
      setIsLoading(false);
    }
  };

  const handleBack = () => {
    setActiveStep((prevStep) => prevStep - 1);
    setError("");
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: 'background.default'
      }}
    >
      {/* Top Banner */}
      <Box
        sx={{
          bgcolor: '#f9a91e',
          color: 'text.primary',
          py: 1.5,
          textAlign: 'center',
          fontWeight: 'bold',
          fontSize: { xs: '0.875rem', sm: '1rem' },
          letterSpacing: 1,
          boxShadow: 1
        }}
      >
        SALES REP PORTAL
      </Box>

      {/* Main Content */}
      <Container 
        component="main" 
        maxWidth="sm"
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          py: { xs: 4, sm: 8 }
        }}
      >
        {/* Back to Login Link */}
        <Box sx={{ mb: 2 }}>
          <Link 
            to="/" 
            style={{ 
              color: '#1d5576', 
              textDecoration: 'none',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '4px',
              fontSize: '0.875rem'
            }}
          >
            <ArrowBack fontSize="small" /> Back to Login
          </Link>
        </Box>

        {/* Reset Password Card */}
        <Paper
          elevation={0}
          sx={{
            p: { xs: 3, sm: 4 },
            borderRadius: 3,
            border: '1px solid',
            borderColor: 'divider',
            bgcolor: 'background.paper'
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              mb: 3
            }}
          >
            <Box
              component="img"
              src="//gibsonhomewares.com/cdn/shop/files/GibsonHomewares_Logo_140x@2x.png"
              alt="Gibson Homewares"
              sx={{
                height: 40,
                width: 'auto',
                maxWidth: 140,
                mb: 2,
              }}
            />
            <Typography
              variant="h5"
              sx={{
                color: '#1d5576',
                fontWeight: 600,
                fontSize: { xs: '1.1rem', sm: '1.25rem' }
              }}
            >
              Reset Password
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mt: 1, textAlign: 'center' }}
            >
              {activeStep === 0 && "Enter your email to receive a reset code"}
              {activeStep === 1 && "Enter the 6-digit code sent to your email"}
              {activeStep === 2 && "Create your new password"}
            </Typography>
          </Box>

          {error && (
            <Alert 
              severity="error" 
              sx={{ 
                mb: 3,
                borderRadius: 2
              }}
            >
              {error}
            </Alert>
          )}

          {success && (
            <Alert 
              severity="success" 
              sx={{ 
                mb: 3,
                borderRadius: 2
              }}
            >
              {success}
            </Alert>
          )}

          {/* Stepper */}
          <Stepper activeStep={activeStep} orientation="vertical" sx={{ mb: 3 }}>
            <Step>
              <StepLabel>Request Reset Code</StepLabel>
              <StepContent>
                <form onSubmit={handleRequestCode}>
                  <TextField
                    margin="normal"
                    required
                    fullWidth
                    id="email"
                    label="Email Address"
                    name="email"
                    autoComplete="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <EmailOutlined color="action" />
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                      }
                    }}
                  />
                  <Box sx={{ mt: 2 }}>
                    <Button
                      type="submit"
                      variant="contained"
                      disabled={isLoading || !email}
                      sx={{
                        mr: 1,
                        bgcolor: '#1d5576',
                        '&:hover': {
                          bgcolor: '#17465f',
                        }
                      }}
                    >
                      {isLoading ? <CircularProgress size={24} /> : 'Send Code'}
                    </Button>
                  </Box>
                </form>
              </StepContent>
            </Step>

            <Step>
              <StepLabel>Verify Code</StepLabel>
              <StepContent>
                <form onSubmit={handleVerifyCode}>
                  <TextField
                    margin="normal"
                    required
                    fullWidth
                    id="resetCode"
                    label="6-Digit Verification Code"
                    name="resetCode"
                    value={resetCode}
                    onChange={(e) => setResetCode(e.target.value)}
                    inputProps={{ maxLength: 6 }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                      }
                    }}
                  />
                  <Box sx={{ mt: 2 }}>
                    <Button
                      type="submit"
                      variant="contained"
                      disabled={isLoading || resetCode.length !== 6}
                      sx={{
                        mr: 1,
                        bgcolor: '#1d5576',
                        '&:hover': {
                          bgcolor: '#17465f',
                        }
                      }}
                    >
                      {isLoading ? <CircularProgress size={24} /> : 'Verify Code'}
                    </Button>
                    <Button
                      onClick={handleBack}
                      sx={{ ml: 1 }}
                    >
                      Back
                    </Button>
                  </Box>
                </form>
              </StepContent>
            </Step>

            <Step>
              <StepLabel>Reset Password</StepLabel>
              <StepContent>
                <form onSubmit={handleResetPassword}>
                  <TextField
                    margin="normal"
                    required
                    fullWidth
                    name="newPassword"
                    label="New Password"
                    type="password"
                    id="newPassword"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <LockOutlined color="action" />
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                      }
                    }}
                  />
                  <TextField
                    margin="normal"
                    required
                    fullWidth
                    name="confirmPassword"
                    label="Confirm Password"
                    type="password"
                    id="confirmPassword"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <LockOutlined color="action" />
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                      }
                    }}
                  />
                  <Box sx={{ mt: 2 }}>
                    <Button
                      type="submit"
                      variant="contained"
                      disabled={isLoading || !newPassword || !confirmPassword}
                      sx={{
                        mr: 1,
                        bgcolor: '#1d5576',
                        '&:hover': {
                          bgcolor: '#17465f',
                        }
                      }}
                    >
                      {isLoading ? <CircularProgress size={24} /> : 'Reset Password'}
                    </Button>
                    <Button
                      onClick={handleBack}
                      sx={{ ml: 1 }}
                    >
                      Back
                    </Button>
                  </Box>
                </form>
              </StepContent>
            </Step>
          </Stepper>
        </Paper>

        {/* Footer */}
        <Box
          sx={{
            mt: 4,
            textAlign: 'center'
          }}
        >
          <Typography
            variant="caption"
            color="text.secondary"
          >
            © {new Date().getFullYear()} Gibson Homewares. All rights reserved.
          </Typography>
        </Box>
      </Container>
    </Box>
  );
}

export default ForgotPassword;